/**
 * 화면 스타일 샘플 1 — 카드형 워크벤치
 * type2(클로브AI 영감)에서 카드·여백이 두드러진다. 동작은 조회 전용.
 */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { PageContainer } from 'styles/common.styles';
import { cssVar, theme } from 'styles/theme';
import { UI_CHROME_CATALOG, normalizeUiChromeType } from 'styles/uiChrome';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';

const Banner = styled.div`
  flex-shrink: 0;
  padding: 12px 14px;
  border-radius: var(--erp-radius-panel, 8px);
  background: ${cssVar('color-bg-white', theme.colors.white)};
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  box-shadow: var(--erp-panel-shadow, none);
`;

const BannerTitle = styled.div`
  font-size: ${theme.typography.fontSize.md};
  font-weight: 700;
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const BannerHint = styled.p`
  margin: 6px 0 0;
  font-size: ${theme.typography.fontSize.xs};
  line-height: 1.5;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--erp-content-gap, 8px);
  flex-shrink: 0;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const KpiCard = styled.div`
  padding: 16px 18px;
  border-radius: var(--erp-radius-panel, 8px);
  background: ${cssVar('color-bg-white', theme.colors.white)};
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  box-shadow: var(--erp-panel-shadow, none);
`;

const KpiLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-tertiary', theme.colors.text.tertiary)};
`;

const KpiValue = styled.div`
  margin-top: 8px;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: 700;
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const KpiSub = styled.div`
  margin-top: 4px;
  font-size: ${theme.typography.fontSize.xxs};
  color: ${cssVar('color-primary', theme.colors.primary)};
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: var(--erp-content-gap, 8px);
  flex: 1;
  min-height: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 16px 18px;
  border-radius: var(--erp-radius-panel, 8px);
  background: ${cssVar('color-bg-white', theme.colors.white)};
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  box-shadow: var(--erp-panel-shadow, none);
`;

const PanelTitle = styled.div`
  flex-shrink: 0;
  margin-bottom: 10px;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 700;
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const Feed = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: auto;
  min-height: 0;
`;

const FeedItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${cssVar('color-border-light', theme.colors.borderLight)};
  font-size: ${theme.typography.fontSize.sm};
`;

const FeedName = styled.span`
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const FeedAmt = styled.span`
  flex-shrink: 0;
  font-weight: 600;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
`;

const KPI = [
  { label: '오늘 처리', value: '18', sub: '결의·승인' },
  { label: '승인 대기', value: '5', sub: '담당 확인' },
  { label: '미마감', value: '2', sub: '차수' },
  { label: '예산 잔액', value: '128,400', sub: '천원' },
];

const FEED = [
  { name: '사무용품 구입 결의', amt: '420' },
  { name: '교육비 원인행위', amt: '1,200' },
  { name: '시설유지 품의', amt: '3,500' },
  { name: '출장여비 정산', amt: '86' },
  { name: '공공요금 이체', amt: '210' },
];

const ChromeCardWorkbench = () => {
  const erpTheme = useErpThemeOptional();
  const chromeId = normalizeUiChromeType(erpTheme?.resolvedAppearance?.uiChromeType);
  const chrome = useMemo(
    () => UI_CHROME_CATALOG.find((x) => x.id === chromeId) || UI_CHROME_CATALOG[0],
    [chromeId]
  );

  return (
    <PageContainer>
      <Banner>
        <BannerTitle>
          카드 워크벤치 · 현재 {chrome.label} ({chromeId})
        </BannerTitle>
        <BannerHint>
          환경설정 → 표시·테마·글꼴에서 화면 스타일을 바꾸면 이 카드의 모서리·그림자·간격이 따라갑니다.
          type2는 클로브AI처럼 흰 패널이 회색 캔버스 위에 떠 있는 형태입니다. 로고·카피는 복제하지 않았습니다.
        </BannerHint>
      </Banner>
      <KpiRow>
        {KPI.map((item) => (
          <KpiCard key={item.label}>
            <KpiLabel>{item.label}</KpiLabel>
            <KpiValue>{item.value}</KpiValue>
            <KpiSub>{item.sub}</KpiSub>
          </KpiCard>
        ))}
      </KpiRow>
      <Split>
        <Panel>
          <PanelTitle>최근 처리</PanelTitle>
          <Feed>
            {FEED.map((row) => (
              <FeedItem key={row.name}>
                <FeedName>{row.name}</FeedName>
                <FeedAmt>{row.amt}천원</FeedAmt>
              </FeedItem>
            ))}
          </Feed>
        </Panel>
        <Panel>
          <PanelTitle>이 샘플이 보여주는 것</PanelTitle>
          <BannerHint as="div">
            카드 vs 밀집 그리드 — 숫·목록을 표 한 장에 붙이지 않고 패널로 나눕니다.
            type1이면 그림자가 거의 없고 간격이 좁습니다. type2면 카드가 떠 보이고 조회바·버튼도 커집니다.
            사이드바 폭·메뉴 높이도 같이 바뀌는지 왼쪽 메뉴를 확인하세요.
          </BannerHint>
        </Panel>
      </Split>
    </PageContainer>
  );
};

export default ChromeCardWorkbench;
